import {
  AnalyticsAsOfQuery,
  AnalyticsBrokerContextCurveRequest,
  AnalyticsBrokerFileParseRequest,
  AnalyticsBrokerFileParseResult,
  AnalyticsBrokerList,
  AnalyticsCanonicalPortfolio,
  AnalyticsCanonicalPortfolioGreeks,
  AnalyticsCanonicalPortfolioValuation,
  AnalyticsCoverageValidation,
  AnalyticsCoverageValidationRequest,
  AnalyticsCurve,
  AnalyticsCurveBuild,
  AnalyticsCurveBuildRequest,
  AnalyticsCurveValidation,
  AnalyticsDemoSummary,
  AnalyticsErrorResponse,
  AnalyticsFredHistory,
  AnalyticsFredHistoryRequest,
  AnalyticsGalaxyIpersParseRequest,
  AnalyticsGalaxyIpersParseResult,
  AnalyticsHealth,
  AnalyticsHistoricalRisk,
  AnalyticsHistoricalVar,
  AnalyticsHistoricalVarRequest,
  AnalyticsLadders,
  AnalyticsMultiAssetSchema,
  AnalyticsMultiAssetVar,
  AnalyticsMultiAssetVarRequest,
  AnalyticsOfficialSofrLatest,
  AnalyticsOfficialTransactionsCurveRequest,
  AnalyticsPortfolioPositions,
  AnalyticsPortfolioRequest,
  AnalyticsPortfolioSummary,
  AnalyticsPublicSnapshotCurveRequest,
  AnalyticsRiskPack,
  AnalyticsSensitivities,
  AnalyticsSourceAnalyticsCurveRequest,
  AnalyticsStationarityValidation,
  AnalyticsStationarityValidationRequest,
  AnalyticsTransactionsCurveRequest,
  AnalyticsValuation,
  AnalyticsVarDiagnostics,
} from '../interfaces/analytics';
import { json, problem, type PathDef } from '../../util/pathDef';

/** Analytics failures use the HFAnalytics `{ error }` body; 401 comes from the shared authorizer. */
const analyticsError = (description: string) => json(description, AnalyticsErrorResponse);

const readFailures = {
  401: problem('Unauthorized'),
  500: analyticsError('Calculation failed (INTERNAL_CALCULATION_ERROR)'),
  502: analyticsError('Analytics unavailable (ANALYTICS_UNAVAILABLE, INVALID_ANALYTICS_RESPONSE)'),
};

const downstream = {
  ...readFailures,
  422: analyticsError('Invalid request (REQUEST_VALIDATION_FAILED, INVALID_ANALYTICS_INPUT, VAR_INPUT_VALIDATION_FAILED, HTTP_ERROR)'),
  500: analyticsError('Calculation failed (INTERNAL_CALCULATION_ERROR or a VaR / pricing code)'),
};

// ---------------------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------------------

export const analyticsAdaptersGalaxyIpersParse: PathDef = {
  method: 'post',
  path: '/analytics/adapters/galaxy-ipers/parse',
  handler: 'domains/bff/src/analytics/adapters/galaxyIpersParse/index.handler',
  tags: ['analytics'],
  summary: 'Parse Galaxy/IPERS position and attribution rows',
  description:
    'Normalizes raw Galaxy/IPERS position and attribution records into canonical positions and a separate cashflow ledger with validation counts, source hashes, lineage and warnings. No valuation is performed. Invokes analytics parseGalaxy.',
  security: 'bearerAuth',
  request: AnalyticsGalaxyIpersParseRequest,
  responses: {
    200: json('Canonical positions and cashflows', AnalyticsGalaxyIpersParseResult),
    ...downstream,
  },
};

export const analyticsAdaptersBrokersGet: PathDef = {
  method: 'get',
  path: '/analytics/adapters/brokers',
  handler: 'domains/bff/src/analytics/adapters/brokers/index.handler',
  tags: ['analytics'],
  summary: 'List supported broker adapters',
  description: 'Broker adapter ids/names plus the shared canonical position fields and live instrument types. Invokes analytics listBrokers.',
  security: 'bearerAuth',
  responses: {
    200: json('Broker adapters', AnalyticsBrokerList),
    ...readFailures,
  },
};

export const analyticsAdaptersBrokerFileParse: PathDef = {
  method: 'post',
  path: '/analytics/adapters/broker-file/parse',
  handler: 'domains/bff/src/analytics/adapters/brokerFileParse/index.handler',
  tags: ['analytics'],
  summary: 'Parse one broker CSV/XLS/XLSX statement',
  description:
    'Normalizes a broker file (`content` for CSV, `content_base64` for XLS/XLSX) into canonical positions and retains incomplete positions for review. Invokes analytics parseBroker.',
  security: 'bearerAuth',
  request: AnalyticsBrokerFileParseRequest,
  responses: {
    200: json('Canonical positions from the broker file', AnalyticsBrokerFileParseResult),
    ...downstream,
  },
};

// ---------------------------------------------------------------------------------------
// Curves / market data
// ---------------------------------------------------------------------------------------

export const analyticsCurvesSofrBuild: PathDef = {
  method: 'post',
  path: '/analytics/curves/sofr/build',
  handler: 'domains/bff/src/analytics/curves/build/index.handler',
  tags: ['analytics'],
  summary: 'Build a USD SOFR curve from controlled quotes',
  description:
    'Bootstraps a QuantLib SOFR OIS curve from controlled input quotes and returns the deterministic curve id, qualification status, normalized quotes, input controls, zero rates, discount factors and warnings. Invokes analytics buildCurve (operation build).',
  security: 'bearerAuth',
  request: AnalyticsCurveBuildRequest,
  responses: {
    200: json('Built SOFR curve', AnalyticsCurveBuild),
    ...downstream,
  },
};

export const analyticsCurvesSofrValidate: PathDef = {
  method: 'post',
  path: '/analytics/curves/sofr/validate',
  handler: 'domains/bff/src/analytics/curves/validate/index.handler',
  tags: ['analytics'],
  summary: 'Validate SOFR curve inputs',
  description: 'Validates and bootstraps curve inputs without returning the full curve grid. Invokes analytics buildCurve (operation validate).',
  security: 'bearerAuth',
  request: AnalyticsCurveBuildRequest,
  responses: {
    200: json('Curve id, status, input controls and warnings', AnalyticsCurveValidation),
    ...downstream,
  },
};

export const analyticsMarketDataFredHistory: PathDef = {
  method: 'post',
  path: '/analytics/market-data/fred/history',
  handler: 'domains/bff/src/analytics/marketData/fredHistory/index.handler',
  tags: ['analytics'],
  summary: 'Fetch the official FRED rate panel',
  description:
    'Pulls the official FRED SOFR/Treasury panel between start_date and end_date for historical shocks. Invokes analytics buildCurve (operation fredHistory).',
  security: 'bearerAuth',
  request: AnalyticsFredHistoryRequest,
  responses: {
    200: json('FRED rate panel', AnalyticsFredHistory),
    ...downstream,
  },
};

export const analyticsMarketDataNyFedSofrLatestGet: PathDef = {
  method: 'get',
  path: '/analytics/market-data/new-york-fed/sofr/latest',
  handler: 'domains/bff/src/analytics/marketData/nyFedSofrLatest/index.handler',
  tags: ['analytics'],
  summary: 'Latest official SOFR observation',
  description:
    'Latest New York Fed SOFR observation available on or before `as_of` (default today). Invokes analytics buildCurve (operation nyfedLatest).',
  security: 'bearerAuth',
  query: AnalyticsAsOfQuery,
  responses: {
    200: json('Official SOFR observation with source lineage', AnalyticsOfficialSofrLatest),
    ...downstream,
  },
};

export const analyticsCurvesSofrFromOfficialTransactions: PathDef = {
  method: 'post',
  path: '/analytics/curves/sofr/from-official-transactions',
  handler: 'domains/bff/src/analytics/curves/fromOfficialTransactions/index.handler',
  tags: ['analytics'],
  summary: 'Build a SOFR curve from official SDR transactions',
  description:
    'Builds from the official NY Fed SOFR fixing plus public records from a CFTC-registered swap data repository. Invokes analytics buildCurve (operation fromOfficialTransactions).',
  security: 'bearerAuth',
  request: AnalyticsOfficialTransactionsCurveRequest,
  responses: {
    200: json('Transaction-derived SOFR curve', AnalyticsCurve),
    ...downstream,
  },
};

export const analyticsCurvesSofrFromBrokerContext: PathDef = {
  method: 'post',
  path: '/analytics/curves/sofr/from-broker-context',
  handler: 'domains/bff/src/analytics/curves/fromBrokerContext/index.handler',
  tags: ['analytics'],
  summary: 'Build a SOFR curve aligned to a broker statement',
  description:
    'Fetches exact-date public DTCC records and aligns aggregation to the broker valuation time. Invokes analytics buildCurve (operation fromBrokerContext).',
  security: 'bearerAuth',
  request: AnalyticsBrokerContextCurveRequest,
  responses: {
    200: json('Broker-context SOFR curve with DTCC archive lineage', AnalyticsCurve),
    ...downstream,
  },
};

export const analyticsCurvesSofrFromPublicSnapshot: PathDef = {
  method: 'post',
  path: '/analytics/curves/sofr/from-public-snapshot',
  handler: 'domains/bff/src/analytics/curves/fromPublicSnapshot/index.handler',
  tags: ['analytics'],
  summary: 'Build an independent SOFR curve from a public snapshot',
  description:
    'Builds an independent, non-production curve from a dated public (CheckMySwap) snapshot with exact as-of-date equality enforced. Invokes analytics buildCurve (operation fromPublicSnapshot).',
  security: 'bearerAuth',
  request: AnalyticsPublicSnapshotCurveRequest,
  responses: {
    200: json('Independent non-production SOFR curve', AnalyticsCurve),
    ...downstream,
  },
};

export const analyticsCurvesSofrFromSourceAnalytics: PathDef = {
  method: 'post',
  path: '/analytics/curves/sofr/from-source-analytics',
  handler: 'domains/bff/src/analytics/curves/fromSourceAnalytics/index.handler',
  tags: ['analytics'],
  summary: 'Reconcile broker valuation observations',
  description:
    'Inspects broker valuation observations without treating them as OIS nodes; returns a reconciliation-only derivation audit. Invokes analytics buildCurve (operation fromSourceAnalytics).',
  security: 'bearerAuth',
  request: AnalyticsSourceAnalyticsCurveRequest,
  responses: {
    200: json('Source valuation reconciliation', AnalyticsCurve),
    ...downstream,
  },
};

export const analyticsCurvesSofrPost: PathDef = {
  method: 'post',
  path: '/analytics/curves/sofr',
  handler: 'domains/bff/src/analytics/curves/fromPortfolio/index.handler',
  tags: ['analytics'],
  summary: 'Build the valuation SOFR curve for a portfolio request',
  description:
    'Runs the rates-analytics curve section for a PortfolioAnalysisRequest and returns analysis_date, valuation_curve_source, curve_quotes, zero_curve and warnings. Invokes analytics buildCurve (operation fromPortfolio).',
  security: 'bearerAuth',
  request: AnalyticsPortfolioRequest,
  responses: {
    200: json('Valuation SOFR curve', AnalyticsCurve),
    ...downstream,
  },
};

export const analyticsCurvesSofrFromQuotes: PathDef = {
  method: 'post',
  path: '/analytics/curves/sofr/from-quotes',
  handler: 'domains/bff/src/analytics/curves/fromQuotes/index.handler',
  tags: ['analytics'],
  summary: 'Build a SOFR curve from client quotes',
  description:
    'Builds from client/vendor par OIS quotes or zero-rate nodes supplied in `ois_quotes`. Invokes analytics buildCurve (operation fromQuotes).',
  security: 'bearerAuth',
  request: AnalyticsPortfolioRequest,
  responses: {
    200: json('Quote-derived SOFR curve', AnalyticsCurve),
    ...downstream,
  },
};

export const analyticsCurvesSofrFromTransactions: PathDef = {
  method: 'post',
  path: '/analytics/curves/sofr/from-transactions',
  handler: 'domains/bff/src/analytics/curves/fromTransactions/index.handler',
  tags: ['analytics'],
  summary: 'Build a SOFR curve from caller-supplied transactions (deprecated)',
  description:
    'Compatibility route for caller-supplied transactions and overnight rate. New integrations use from-official-transactions. Invokes analytics buildCurve (operation fromTransactions).',
  security: 'bearerAuth',
  request: AnalyticsTransactionsCurveRequest,
  responses: {
    200: json('Transaction-derived SOFR curve with quality and benchmark validation', AnalyticsCurve),
    ...downstream,
  },
};

// ---------------------------------------------------------------------------------------
// Valuation / portfolio / risk
// ---------------------------------------------------------------------------------------

export const analyticsValuationRatesPortfolio: PathDef = {
  method: 'post',
  path: '/analytics/valuation/rates-portfolio',
  handler: 'domains/bff/src/analytics/valuation/ratesPortfolio/index.handler',
  tags: ['analytics'],
  summary: 'Value a rates portfolio',
  description: 'Position valuations, curve roles and conventions for a PortfolioAnalysisRequest. Invokes analytics price (slice valuation).',
  security: 'bearerAuth',
  request: AnalyticsPortfolioRequest,
  responses: {
    200: json('Portfolio valuation', AnalyticsValuation),
    ...downstream,
  },
};

export const analyticsValuationIrs: PathDef = {
  method: 'post',
  path: '/analytics/valuation/irs',
  handler: 'domains/bff/src/analytics/valuation/irs/index.handler',
  tags: ['analytics'],
  summary: 'Value IRS positions (alias of rates-portfolio)',
  description:
    'Backward-compatible alias of /analytics/valuation/rates-portfolio; non-IRS rows are also accepted. Invokes analytics price (slice valuation).',
  security: 'bearerAuth',
  request: AnalyticsPortfolioRequest,
  responses: {
    200: json('Portfolio valuation', AnalyticsValuation),
    ...downstream,
  },
};

export const analyticsPortfolioSummary: PathDef = {
  method: 'post',
  path: '/analytics/portfolio/summary',
  handler: 'domains/bff/src/analytics/portfolio/summary/index.handler',
  tags: ['analytics'],
  summary: 'Portfolio summary and exposure',
  description: 'Portfolio positions, exposure, curve roles and conventions for a PortfolioAnalysisRequest. Invokes analytics price (slice summary).',
  security: 'bearerAuth',
  request: AnalyticsPortfolioRequest,
  responses: {
    200: json('Portfolio summary', AnalyticsPortfolioSummary),
    ...downstream,
  },
};

export const analyticsPortfolioPositions: PathDef = {
  method: 'post',
  path: '/analytics/portfolio/positions',
  handler: 'domains/bff/src/analytics/portfolio/positions/index.handler',
  tags: ['analytics'],
  summary: 'Per-position valuations',
  description: 'Position-level valuation rows for a PortfolioAnalysisRequest. Invokes analytics price (slice positions).',
  security: 'bearerAuth',
  request: AnalyticsPortfolioRequest,
  responses: {
    200: json('Position valuations', AnalyticsPortfolioPositions),
    ...downstream,
  },
};

export const analyticsRiskHistorical: PathDef = {
  method: 'post',
  path: '/analytics/risk/historical',
  handler: 'domains/bff/src/analytics/risk/historical/index.handler',
  tags: ['analytics'],
  summary: 'Historical risk for a rates portfolio',
  description:
    'Historical VaR / expected shortfall from FRED-driven curve shocks (original analyze_portfolio historical section, one Lambda invoke). Invokes analytics scenarioPnl with the raw request.',
  security: 'bearerAuth',
  request: AnalyticsPortfolioRequest,
  responses: {
    200: json('Historical risk', AnalyticsHistoricalRisk),
    ...downstream,
  },
};

export const analyticsRiskLadders: PathDef = {
  method: 'post',
  path: '/analytics/risk/ladders',
  handler: 'domains/bff/src/analytics/risk/ladders/index.handler',
  tags: ['analytics'],
  summary: 'Rate and vol shock ladders',
  description: 'P&L ladders over ladder_rate_shocks_bp and ladder_normal_vol_shocks_bp. Invokes analytics ladders.',
  security: 'bearerAuth',
  request: AnalyticsPortfolioRequest,
  responses: {
    200: json('Shock ladders', AnalyticsLadders),
    ...downstream,
  },
};

export const analyticsRiskSensitivities: PathDef = {
  method: 'post',
  path: '/analytics/risk/sensitivities',
  handler: 'domains/bff/src/analytics/risk/sensitivities/index.handler',
  tags: ['analytics'],
  summary: 'Position and portfolio sensitivities',
  description: 'DV01, rate gamma, theta, vega and key-rate DV01 per position and portfolio. Invokes analytics sensitivities.',
  security: 'bearerAuth',
  request: AnalyticsPortfolioRequest,
  responses: {
    200: json('Sensitivities', AnalyticsSensitivities),
    ...downstream,
  },
};

export const analyticsPortfolioRiskPack: PathDef = {
  method: 'post',
  path: '/analytics/portfolio/risk-pack',
  handler: 'domains/bff/src/analytics/portfolio/riskPack/index.handler',
  tags: ['analytics'],
  summary: 'Valuation + sensitivities + ladders in one call',
  description:
    'BFF aggregate: invokes analytics price, sensitivities and ladders in parallel with the same PortfolioAnalysisRequest and returns { valuation, sensitivities, ladders }. Single-purpose routes remain available.',
  security: 'bearerAuth',
  request: AnalyticsPortfolioRequest,
  responses: {
    200: json('Risk pack', AnalyticsRiskPack),
    ...downstream,
  },
};

export const analyticsDemoGalaxyIpersSummaryGet: PathDef = {
  method: 'get',
  path: '/analytics/demo/galaxy-ipers/summary',
  handler: 'domains/bff/src/analytics/demo/galaxyIpersSummary/index.handler',
  tags: ['analytics'],
  summary: 'Recalculate a frozen Galaxy/IPERS demo snapshot',
  description: 'Demonstration only: recalculates a registered frozen Galaxy/IPERS snapshot for `as_of` (default 2024-01-23). Invokes analytics demo.',
  security: 'bearerAuth',
  query: AnalyticsAsOfQuery,
  responses: {
    200: json('Demo summary', AnalyticsDemoSummary),
    ...downstream,
    404: analyticsError('Demo disabled or unknown snapshot (HTTP_ERROR)'),
  },
};

// ---------------------------------------------------------------------------------------
// VaR
// ---------------------------------------------------------------------------------------

export const analyticsVarDiagnosticsHealthGet: PathDef = {
  method: 'get',
  path: '/analytics/var/diagnostics/health',
  handler: 'domains/bff/src/analytics/var/diagnosticsHealth/index.handler',
  tags: ['analytics'],
  summary: 'VaR engine self-test',
  description: 'Exercises the VaR calculation path and its principal release controls. Invokes analytics var (operation diagnostics).',
  security: 'bearerAuth',
  responses: {
    200: json('VaR engine diagnostics', AnalyticsVarDiagnostics),
    ...readFailures,
  },
};

export const analyticsVarHistorical: PathDef = {
  method: 'post',
  path: '/analytics/var/historical',
  handler: 'domains/bff/src/analytics/var/historical/index.handler',
  tags: ['analytics'],
  summary: 'Historical VaR from a P&L matrix',
  description:
    'Aggregates an asset-agnostic scenario-by-position P&L matrix into weighted empirical VaR and expected shortfall with reconciled allocations and tail diagnostics. Invokes analytics var (operation historical).',
  security: 'bearerAuth',
  request: AnalyticsHistoricalVarRequest,
  responses: {
    200: json('Historical VaR', AnalyticsHistoricalVar),
    ...downstream,
  },
};

export const analyticsVarMultiAssetSchemaGet: PathDef = {
  method: 'get',
  path: '/analytics/var/multi-asset/schema',
  handler: 'domains/bff/src/analytics/var/multiAssetSchema/index.handler',
  tags: ['analytics'],
  summary: 'Multi-asset VaR request JSON Schema',
  description: 'Authoritative JSON Schema accepted by POST /analytics/var/multi-asset. Invokes analytics multiAsset (operation schema).',
  security: 'bearerAuth',
  responses: {
    200: json('JSON Schema', AnalyticsMultiAssetSchema),
    ...readFailures,
  },
};

export const analyticsVarValidationCoverage: PathDef = {
  method: 'post',
  path: '/analytics/var/validation/coverage',
  handler: 'domains/bff/src/analytics/var/validationCoverage/index.handler',
  tags: ['analytics'],
  summary: 'VaR coverage back-test',
  description: 'Validates aligned out-of-sample VaR forecasts against realised losses. Invokes analytics var (operation coverage).',
  security: 'bearerAuth',
  request: AnalyticsCoverageValidationRequest,
  responses: {
    200: json('Coverage checks', AnalyticsCoverageValidation),
    ...downstream,
  },
};

export const analyticsVarValidationStationarity: PathDef = {
  method: 'post',
  path: '/analytics/var/validation/stationarity',
  handler: 'domains/bff/src/analytics/var/validationStationarity/index.handler',
  tags: ['analytics'],
  summary: 'Factor stationarity diagnostics',
  description: 'ADF/KPSS marginal checks and a joint VAR stability diagnostic on factor history. Invokes analytics var (operation stationarity).',
  security: 'bearerAuth',
  request: AnalyticsStationarityValidationRequest,
  responses: {
    200: json('Stationarity diagnostics', AnalyticsStationarityValidation),
    ...downstream,
  },
};

export const analyticsVarPrice: PathDef = {
  method: 'post',
  path: '/analytics/var/price',
  handler: 'domains/bff/src/analytics/var/price/index.handler',
  tags: ['analytics'],
  summary: 'Price a canonical cross-asset portfolio',
  description: 'Prices every canonical holding with the Python cross-asset pricer registry. Invokes analytics multiAsset (operation price).',
  security: 'bearerAuth',
  request: AnalyticsCanonicalPortfolio,
  responses: {
    200: json('Canonical portfolio valuation', AnalyticsCanonicalPortfolioValuation),
    ...downstream,
  },
};

export const analyticsVarGreeks: PathDef = {
  method: 'post',
  path: '/analytics/var/greeks',
  handler: 'domains/bff/src/analytics/var/greeks/index.handler',
  tags: ['analytics'],
  summary: 'Factor delta and gamma for a canonical portfolio',
  description: 'Factor delta and the complete local gamma matrix by central differences. Invokes analytics multiAsset (operation greeks).',
  security: 'bearerAuth',
  request: AnalyticsCanonicalPortfolio,
  responses: {
    200: json('Canonical portfolio greeks', AnalyticsCanonicalPortfolioGreeks),
    ...downstream,
  },
};

export const analyticsVarMultiAsset: PathDef = {
  method: 'post',
  path: '/analytics/var/multi-asset',
  handler: 'domains/bff/src/analytics/var/multiAsset/index.handler',
  tags: ['analytics'],
  summary: 'Multi-asset VaR',
  description:
    'VaR from one canonical cross-asset portfolio-and-scenario document; pricing is separated from aggregation. Invokes analytics multiAsset (operation calculate).',
  security: 'bearerAuth',
  request: AnalyticsMultiAssetVarRequest,
  responses: {
    200: json('Multi-asset VaR', AnalyticsMultiAssetVar),
    ...downstream,
  },
};

// ---------------------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------------------

export const analyticsHealthGet: PathDef = {
  method: 'get',
  path: '/analytics/health',
  handler: 'domains/bff/src/analytics/health/index.handler',
  tags: ['analytics'],
  summary: 'Analytics domain health',
  description: 'Invokes the analytics health Lambda.',
  security: 'bearerAuth',
  responses: {
    200: json('Analytics health', AnalyticsHealth),
    401: problem('Unauthorized'),
    502: problem('Analytics unavailable'),
  },
};

export const analyticsPaths: PathDef[] = [
  analyticsAdaptersGalaxyIpersParse,
  analyticsAdaptersBrokersGet,
  analyticsAdaptersBrokerFileParse,
  analyticsCurvesSofrBuild,
  analyticsCurvesSofrValidate,
  analyticsMarketDataFredHistory,
  analyticsMarketDataNyFedSofrLatestGet,
  analyticsCurvesSofrFromOfficialTransactions,
  analyticsCurvesSofrFromBrokerContext,
  analyticsCurvesSofrFromPublicSnapshot,
  analyticsCurvesSofrFromSourceAnalytics,
  analyticsCurvesSofrPost,
  analyticsCurvesSofrFromQuotes,
  analyticsCurvesSofrFromTransactions,
  analyticsValuationRatesPortfolio,
  analyticsValuationIrs,
  analyticsPortfolioSummary,
  analyticsPortfolioPositions,
  analyticsRiskHistorical,
  analyticsRiskLadders,
  analyticsRiskSensitivities,
  analyticsPortfolioRiskPack,
  analyticsDemoGalaxyIpersSummaryGet,
  analyticsVarDiagnosticsHealthGet,
  analyticsVarHistorical,
  analyticsVarMultiAssetSchemaGet,
  analyticsVarValidationCoverage,
  analyticsVarValidationStationarity,
  analyticsVarPrice,
  analyticsVarGreeks,
  analyticsVarMultiAsset,
  analyticsHealthGet,
];
